import type { UIMessage, JSONValue } from 'ai';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { classNames } from '~/utils/classNames';
import { AssistantMessage } from './AssistantMessage';
import { UserMessage } from './UserMessage';
import { extractMemorySignals, recallKey } from './memory-annotations';
import { chatId } from '~/lib/persistence/useChatHistory';
import { workbenchStore } from '~/lib/stores/workbench';
import { forkChat } from '~/lib/persistence/db';
import { toast } from 'react-toastify';
import { forwardRef } from 'react';
import type { ForwardedRef } from 'react';
import type { ProviderInfo } from '~/types/model';
import { stripSuggestionsBlock } from './suggestion-chips';

function AgentFinishedDivider({ completedAt }: { completedAt?: string }) {
  const timestamp = useMemo(() => {
    const date = completedAt ? new Date(completedAt) : new Date();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    const time = date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    return `${month} ${day}, ${time}`;
  }, [completedAt]);

  return (
    <div className="flex items-center gap-3 py-4 select-none max-w-chat mx-auto w-full">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-subtle to-transparent" />
      <div className="flex items-center gap-2">
        <span className="i-ph:check text-accent text-[11px]" />
        <span className="ui-caption">Done</span>
        <span className="font-geist-mono text-[10.5px] text-faint">{timestamp}</span>
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-subtle to-transparent" />
    </div>
  );
}

/**
 * True when an assistant message ended the turn by asking the user questions
 * that haven't been answered yet. `ask_user` is a stop condition on the agent
 * loop, so the turn is technically finished — but nothing was built, and
 * showing "Done" + Preview/Publish there reads as if the AI gave up waiting.
 */
function isAwaitingAnswer(message: { metadata?: unknown }): boolean {
  const askUser = (message.metadata as { askUser?: { questions?: unknown[] } } | undefined)?.askUser;

  return Array.isArray(askUser?.questions) && askUser.questions.length > 0;
}

/** Shown instead of the "Done" divider while an ask_user prompt is unanswered. */
function AwaitingAnswerDivider() {
  return (
    <div className="flex items-center gap-3 py-4 select-none max-w-chat mx-auto w-full">
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-subtle to-transparent" />
      <div className="flex items-center gap-2">
        <span className="i-ph:chat-circle-dots text-accent text-[12px] ui-pulsing" />
        <span className="ui-caption">Waiting for your answer</span>
      </div>
      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-subtle to-transparent" />
    </div>
  );
}

/**
 * The completed-turn call to action, shown under the "Done" divider: view the
 * finished site (reopens the preview) or publish it. Preview is a neutral pill,
 * Publish the lime primary — matching the header's button system.
 */
function CompletionActions() {
  return (
    <div className="max-w-chat mx-auto w-full flex items-center justify-center gap-2.5 mt-1 mb-5">
      <button
        type="button"
        onClick={() => workbenchStore.reopenWorkbench()}
        className="inline-flex items-center gap-2 rounded-full bg-elevated px-5 py-2.5 text-[14px] font-semibold text-primary transition-colors hover:bg-[#2E2E37]"
      >
        <span className="i-ph:eye text-[16px] text-accent" />
        Preview
      </button>
      <button
        type="button"
        onClick={() => workbenchStore.reopenWorkbench('deployment')}
        className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-[14px] font-semibold text-[#0A0A0A] shadow-[0_4px_18px_-4px_var(--accent-glow)] transition-[filter] hover:brightness-110"
      >
        <span className="i-ph:rocket-launch text-[16px]" />
        Publish
      </button>
    </div>
  );
}

/** Show "Model is thinking…" after this much streaming silence (no new content). */
const STALL_INDICATOR_AFTER_MS = 40_000;

/**
 * Cheap fingerprint of a message's REAL content — new parts or growth of the
 * streaming last part change it; the server's 20s `data-aiHeartbeat` keep-alive
 * parts are excluded. While a build streams, a frozen fingerprint means the
 * model has gone silent (typically a long think / reasoning stall of 1–5 min
 * that recovers on its own), NOT a dead connection (heartbeats still land).
 */
function contentFingerprint(message?: UIMessage): string {
  if (!message) {
    return 'none';
  }

  const parts = ((message.parts as any[]) ?? []).filter((part) => part?.type !== 'data-aiHeartbeat');
  const last = parts[parts.length - 1];
  let lastSize = 0;

  if (last) {
    if (typeof last.text === 'string') {
      lastSize = last.text.length;
    } else if (last.input !== undefined || last.data !== undefined) {
      try {
        lastSize = JSON.stringify(last.input ?? last.data).length;
      } catch {
        lastSize = 0;
      }
    }
  }

  return `${message.id}:${parts.length}:${last?.type ?? ''}:${lastSize}`;
}

/**
 * The streaming footer: "Generating…" dots normally; after 40s with no new
 * content it switches to "Model is thinking…" with a live counter, so a long
 * silent think (which can run several minutes before the model resumes) reads
 * as the model working — not a frozen build. Self-contained state so its 1s
 * tick never re-renders the message list above it.
 */
function GeneratingIndicator({ lastMessage }: { lastMessage?: UIMessage }) {
  const fingerprint = contentFingerprint(lastMessage);
  const lastContentAtRef = useRef(Date.now());
  const [silentSeconds, setSilentSeconds] = useState(0);

  useEffect(() => {
    lastContentAtRef.current = Date.now();
    setSilentSeconds(0);
  }, [fingerprint]);

  useEffect(() => {
    const tick = setInterval(() => {
      const silentMs = Date.now() - lastContentAtRef.current;
      setSilentSeconds(silentMs >= STALL_INDICATOR_AFTER_MS ? Math.floor(silentMs / 1000) : 0);
    }, 1000);

    return () => clearInterval(tick);
  }, []);

  const thinking = silentSeconds > 0;
  const minutes = Math.floor(silentSeconds / 60);
  const seconds = silentSeconds % 60;
  const elapsed = minutes > 0 ? `${minutes}m ${String(seconds).padStart(2, '0')}s` : `${seconds}s`;

  return (
    <div className="flex items-center gap-2.5 px-1 py-3 mt-2">
      <div className="flex gap-1">
        <span
          className={classNames('w-1.5 h-1.5 rounded-full bg-accent', thinking ? 'animate-pulse' : 'animate-bounce')}
          style={{ animationDelay: '0ms' }}
        />
        <span
          className={classNames('w-1.5 h-1.5 rounded-full bg-accent', thinking ? 'animate-pulse' : 'animate-bounce')}
          style={{ animationDelay: '150ms' }}
        />
        <span
          className={classNames('w-1.5 h-1.5 rounded-full bg-accent', thinking ? 'animate-pulse' : 'animate-bounce')}
          style={{ animationDelay: '300ms' }}
        />
      </div>
      {thinking ? (
        <span className="text-[12px] text-muted">
          Model is thinking — longer steps can take a few minutes{' '}
          <span className="font-geist-mono text-[11px] text-faint">({elapsed})</span>
        </span>
      ) : (
        <span className="text-[12px] text-muted">Generating…</span>
      )}
    </div>
  );
}

interface MessagesProps {
  id?: string;
  className?: string;
  isStreaming?: boolean;
  messages?: UIMessage[];
  append?: (message: UIMessage) => void;
  chatMode?: 'discuss' | 'build';
  setChatMode?: (mode: 'discuss' | 'build') => void;
  model?: string;
  provider?: ProviderInfo;
  /** messageIds with a restorable checkpoint — gates the revert button. */
  checkpoints?: Set<string>;
  onRestore?: (messageId: string) => void;
}

export const Messages = forwardRef<HTMLDivElement, MessagesProps>(
  (props: MessagesProps, ref: ForwardedRef<HTMLDivElement> | undefined) => {
    const { id, isStreaming = false, messages = [], checkpoints, onRestore } = props;

    const handleFork = async (messageId: string) => {
      try {
        if (!chatId.get()) {
          toast.error("This isn't ready to fork yet. Please try again.");
          return;
        }

        const urlId = await forkChat(chatId.get()!, messageId);
        window.location.href = `/chat/${urlId}`;
      } catch (error) {
        console.error(error);
        toast.error("Couldn't fork this chat. Please try again.");
      }
    };

    // Deduplicate messages by ID to prevent React key warnings during streaming
    const uniqueMessages = useMemo(() => {
      const seen = new Map<string, UIMessage>();

      for (const msg of messages) {
        if (msg.id) {
          seen.set(msg.id, msg);
        }
      }

      return Array.from(seen.values());
    }, [messages]);

    // The ai service emits a "Using memories" recall signal on every assistant
    // turn, so a single generation can repeat the identical chip several times.
    // Walk the timeline in order and mark the messages whose recall payload
    // matches the last one already shown — those get suppressed, so the chip only
    // reappears when the recalled memories actually change.
    const suppressedRecallIds = useMemo(() => {
      const suppressed = new Set<string>();
      let lastKey: string | null = null;

      for (const message of uniqueMessages) {
        if (message.role !== 'assistant') {
          continue;
        }

        const dataParts = ((message.parts as any[]) ?? [])
          .filter(
            (p) => typeof p?.type === 'string' && p.type.startsWith('data-') && !!p.data && typeof p.data === 'object',
          )
          .map((p) => p.data as JSONValue);
        const legacy = (message as any).annotations as JSONValue[] | undefined;
        const { recall } = extractMemorySignals([...(legacy ?? []), ...dataParts]);

        if (!recall) {
          continue;
        }

        const key = recallKey(recall);

        if (key === lastKey && message.id) {
          suppressed.add(message.id);
        } else {
          lastKey = key;
        }
      }

      return suppressed;
    }, [uniqueMessages]);

    return (
      <div id={id} className={props.className} ref={ref}>
        {uniqueMessages.length > 0
          ? uniqueMessages.map((message, index) => {
              const { role, id: messageId, parts = [] } = message;

              // `content` is the full text joined — used for the copy button and
              // the user-message bubble. The assistant renders from `parts`
              // directly (in order); see AssistantMessage / message-parts.ts.
              const content = stripSuggestionsBlock(
                parts
                  .filter((p): p is { type: 'text'; text: string } => p.type === 'text')
                  .map((p) => p.text)
                  .join(''),
              );
              // AI SDK v6 streams sub-agent progress, file changes, etc. as `data-*`
              // parts (persisted inside message.parts). The annotation-based renderers
              // below expect the inner `data` payloads, so surface them here — this is
              // what makes sub-agent activity reappear when an older chat is reopened.
              const dataAnnotations = (parts as any[])
                .filter(
                  (p) =>
                    typeof p?.type === 'string' && p.type.startsWith('data-') && !!p.data && typeof p.data === 'object',
                )
                .map((p) => p.data as JSONValue);
              const legacyAnnotations = (message as any).annotations as JSONValue[] | undefined;
              const annotations =
                legacyAnnotations || dataAnnotations.length
                  ? [...(legacyAnnotations ?? []), ...dataAnnotations]
                  : undefined;
              const metadata = (message as any).metadata as Record<string, any> | undefined;
              const isUserMessage = role === 'user';
              const isFirst = index === 0;
              const isHidden = annotations?.includes('hidden');

              if (isHidden) {
                return <Fragment key={messageId || `hidden-${index}`} />;
              }

              // Show a "Done" divider after each completed assistant cycle:
              // an assistant message followed by a user message (mid-conversation boundary)
              const nextMessage = uniqueMessages[index + 1];
              const isCycleEnd = !isUserMessage && nextMessage?.role === 'user' && !isAwaitingAnswer(message as any);
              const completedAt = metadata?.completedAt as string | undefined;

              return (
                <Fragment key={messageId || `msg-${index}`}>
                  <div
                    className={classNames('w-full', {
                      'mt-6': !isFirst,
                      // Off-screen completed messages skip layout/paint (heavy-
                      // chat hardening). Never applied to the last message, so
                      // live streaming renders exactly as before.
                      'msg-cv': index < uniqueMessages.length - 1,
                    })}
                  >
                    {isUserMessage ? (
                      <UserMessage content={content} parts={parts as any} />
                    ) : (
                      <AssistantMessage
                        content={content}
                        parts={parts}
                        annotations={annotations}
                        metadata={metadata}
                        messageId={messageId}
                        isLastMessage={index === uniqueMessages.length - 1}
                        isStreaming={isStreaming}
                        hasCheckpoint={messageId ? (checkpoints?.has(messageId) ?? false) : false}
                        hideMemoryRecall={messageId ? suppressedRecallIds.has(messageId) : false}
                        onRestore={onRestore}
                        onFork={handleFork}
                        append={props.append}
                        chatMode={props.chatMode}
                        setChatMode={props.setChatMode}
                        model={props.model}
                        provider={props.provider}
                      />
                    )}
                  </div>
                  {isCycleEnd && <AgentFinishedDivider completedAt={completedAt} />}
                </Fragment>
              );
            })
          : null}
        {isStreaming && <GeneratingIndicator lastMessage={uniqueMessages[uniqueMessages.length - 1]} />}
        {!isStreaming &&
          uniqueMessages.length > 0 &&
          uniqueMessages[uniqueMessages.length - 1].role === 'assistant' &&
          (isAwaitingAnswer(uniqueMessages[uniqueMessages.length - 1] as any) ? (
            <AwaitingAnswerDivider />
          ) : (
            <>
              <AgentFinishedDivider
                completedAt={(uniqueMessages[uniqueMessages.length - 1] as any).metadata?.completedAt}
              />
              <CompletionActions />
            </>
          ))}
      </div>
    );
  },
);
